import { useState, useRef, useCallback, useMemo } from 'react';

// Rapid successive changes (e.g. typing in a field, dragging) within this window collapse into a
// single undo step, so undo reverts a deliberate action rather than one keystroke at a time.
const COALESCE_MS = 500;

/**
 * useState with undo/redo history.
 *
 * Drop-in for `useState`: returns `[present, setPresent, history]` where `setPresent` accepts a
 * value or an updater function exactly like `useState`'s setter, so existing call sites need no
 * changes. `history` exposes `{ undo, redo, reset, canUndo, canRedo }`. Use `reset(value)` for
 * non-undoable baseline changes (e.g. loading a different flow), it sets the value and clears
 * the past/future stacks.
 *
 * @param {*} initialValue Initial value, or a lazy initializer function
 * @param {number} coalesceMs Window within which consecutive edits merge into one undo step
 */
export function useHistoryState(initialValue, coalesceMs = COALESCE_MS) {
  const [stacks, setStacks] = useState(() => ({
    past: [],
    present: typeof initialValue === 'function' ? initialValue() : initialValue,
    future: [],
  }));
  const lastChangeAtRef = useRef(0);
  const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

  const setPresent = useCallback((updater) => {
    setStacks((prev) => {
      const next = typeof updater === 'function' ? updater(prev.present) : updater;
      if (next === prev.present) {
        return prev;
      }
      const now = nowMs();
      // Merge into the current undo step if this change closely follows the previous one.
      const coalesce = prev.past.length > 0 && now - lastChangeAtRef.current < coalesceMs;
      lastChangeAtRef.current = now;
      return {
        past: coalesce ? prev.past : [...prev.past, prev.present],
        present: next,
        future: [],
      };
    });
  }, [coalesceMs]);

  const undo = useCallback(() => {
    lastChangeAtRef.current = 0;
    setStacks((prev) => {
      if (prev.past.length === 0) {
        return prev;
      }
      const previous = prev.past[prev.past.length - 1];
      return {
        past: prev.past.slice(0, -1),
        present: previous,
        future: [prev.present, ...prev.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    lastChangeAtRef.current = 0;
    setStacks((prev) => {
      if (prev.future.length === 0) {
        return prev;
      }
      const [next, ...rest] = prev.future;
      return {
        past: [...prev.past, prev.present],
        present: next,
        future: rest,
      };
    });
  }, []);

  const reset = useCallback((value) => {
    lastChangeAtRef.current = 0;
    setStacks({
      past: [],
      present: typeof value === 'function' ? value() : value,
      future: [],
    });
  }, []);

  const history = useMemo(() => ({
    undo,
    redo,
    reset,
    canUndo: stacks.past.length > 0,
    canRedo: stacks.future.length > 0,
  }), [undo, redo, reset, stacks.past.length, stacks.future.length]);

  return [stacks.present, setPresent, history];
}
